import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  inject,
} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  Validators,
  ValidatorFn,
  ValidationErrors,
  AbstractControl,
} from '@angular/forms';
import {
  FlagsTrackers,
  ICategoriaEvento,
  ICodigoDispositivo,
  ICodigoDispositivoEntrada,
  ICodigosDispositivo,
  ICreateCodigosDispositivo,
  IListadoCategoria,
  IQueryParam,
  IUpdateCodigosDispositivo,
  TipoDispositivo,
} from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import { ListadoCategoriasSelectorComponent } from '../../../../componentes/selectores/listado-categoria-selector/listado-categoria-selector.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { CodigosDispositivosService } from '../../../../auxiliares/servicios/http/codigos-dispositivos.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
  imports: [AuxiliaresModule, ListadoCategoriasSelectorComponent],
  selector: 'app-crear-editar-codigos-dispositivos',
  templateUrl: './crear-editar-codigos-dispositivos.component.html',
  styleUrl: './crear-editar-codigos-dispositivos.component.scss',
})
export class CrearEditarCodigosDispositivosComponent
  implements OnInit, OnDestroy
{
  dialogRef = inject<MatDialogRef<CrearEditarCodigosDispositivosComponent>>(
    MatDialogRef,
    { optional: true },
  );
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private service = inject(CodigosDispositivosService);
  private route = inject(ActivatedRoute);

  public loading = true;
  public form?: FormGroup;
  public title?: string;
  public subtitle?: string;
  public id?: string | null;
  public codigosDispositivo?: ICodigosDispositivo;

  public categoriasEventos: ICategoriaEvento[] = [];
  public categoriasEventos$?: Subscription;

  public initialNamesForCodigos: string[][] = [];
  public initialNamesForCodigosEntrada: string[][] = [];

  public tipo?: TipoDispositivo;

  public flagsTrackersList: FlagsTrackers[] = [
    'Posicion',
    'Ignición',
    'Emergencia',
    'Batería',
  ];

  @ViewChild('codigosContainer') codigosContainer?: ElementRef;

  get arrayCodigos() {
    return this.codigosForm?.controls;
  }
  get codigosForm() {
    return this.form?.get('codigos') as FormArray;
  }

  get arrayCodigosEntrada() {
    return this.codigosEntradaForm?.controls;
  }
  get codigosEntradaForm() {
    return this.form?.get('codigosEntrada') as FormArray;
  }

  public searchCategoria = (term: string, item: ICategoriaEvento) => {
    if (item.nombre?.toLowerCase().includes(term.toLowerCase())) return true;

    return false;
  };

  public onDescripcionChange(i: number, cat: IListadoCategoria | null): void {
    const ctrl = this.codigosForm.at(i).get('descripcion');
    ctrl?.setValue(cat?.nombre ?? null, { emitEvent: true });
    ctrl?.markAsTouched();
    ctrl?.markAsDirty();
    this.codigosForm.updateValueAndValidity();
    this.form?.updateValueAndValidity();
  }

  public onDescripcionEntradaChange(
    i: number,
    cat: IListadoCategoria | null,
  ): void {
    const ctrl = this.codigosEntradaForm.at(i).get('descripcion');
    ctrl?.setValue(cat?.nombre ?? null, { emitEvent: true });
    ctrl?.markAsTouched();
    ctrl?.markAsDirty();
    this.codigosEntradaForm.updateValueAndValidity();
    this.form?.updateValueAndValidity();
  }

  public hasFlagTracker(group: AbstractControl, flag: FlagsTrackers): boolean {
    return (group.get('flagsTrackers')?.value as FlagsTrackers[])?.includes(
      flag,
    );
  }

  public toggleFlagTracker(
    group: AbstractControl,
    flag: FlagsTrackers,
    checked: boolean,
  ): void {
    const control = group.get('flagsTrackers');
    const current: FlagsTrackers[] = control?.value ?? [];
    control?.setValue(
      checked ? [...current, flag] : current.filter((f) => f !== flag),
    );
  }

  /// Drag and Drop
  public drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.arrayCodigos, event.previousIndex, event.currentIndex);
    // Patch formArray
    this.codigosForm.patchValue(this.arrayCodigos);
  }

  public dropCodigoEntrada(event: CdkDragDrop<string[]>): void {
    moveItemInArray(
      this.arrayCodigosEntrada,
      event.previousIndex,
      event.currentIndex,
    );
    // Patch formArray
    this.codigosEntradaForm.patchValue(this.arrayCodigosEntrada);
  }

  private addNewCodigo() {
    const codigos = [];
    if (this.codigosDispositivo?.codigos) {
      for (const r of this.codigosDispositivo.codigos) {
        codigos.push(this.addCodigoFormGroup(r));
      }
    } else {
      codigos.push(this.addCodigoFormGroup());
    }
    return codigos;
  }

  private addNewCodigoEntrada() {
    const codigos = [];
    if (this.codigosDispositivo?.codigosEntrada?.length) {
      for (const r of this.codigosDispositivo.codigosEntrada) {
        codigos.push(this.addCodigoFormGroupEntrada(r));
      }
    }
    return codigos;
  }

  private createForm() {
    const formConfig: any = {
      nombre: new FormControl(this.codigosDispositivo?.nombre),
      codigos: new FormArray(this.addNewCodigo(), {
        validators: [this.codigoUnicoValidator()],
      }),
      tipo: new FormControl(this.tipo),
      global: new FormControl(this.codigosDispositivo?.global || false),
    };

    // Solo agregar codigosEntrada si el tipo es Tracker
    if (this.tipo === 'Tracker') {
      formConfig.codigosEntrada = new FormArray(this.addNewCodigoEntrada(), {
        validators: [this.codigoUnicoValidator()],
      });
    }

    this.form = new FormGroup(formConfig);
    this.loading = false;
  }

  public addCodigo() {
    this.codigosForm.push(this.addCodigoFormGroup());
    // Hacer scroll al final después de agregar
    setTimeout(() => {
      if (this.codigosContainer) {
        this.codigosContainer.nativeElement.scrollTop =
          this.codigosContainer.nativeElement.scrollHeight;
      }
    }, 100);
  }

  private addCodigoFormGroup(codigo?: ICodigoDispositivo) {
    return new FormGroup({
      codigo: new FormControl(codigo?.codigo, [Validators.required]),
      descripcion: new FormControl(codigo?.descripcion, Validators.required),
      idCategoriaEvento: new FormControl(codigo?.idCategoriaEvento, [
        Validators.required,
      ]),
      mostrarZona: new FormControl(codigo?.mostrarZona ?? false),
      mostrarUsuario: new FormControl(codigo?.mostrarUsuario ?? false),
      armado: new FormControl(codigo?.armado ?? false),
      desarmado: new FormControl(codigo?.desarmado ?? false),
      detonacion: new FormControl(codigo?.detonacion ?? false),
      test: new FormControl(codigo?.test ?? false),
      notificar: new FormControl(codigo?.notificar ?? false),
      minutosEsperaAutomatica: new FormControl(
        codigo?.minutosEsperaAutomatica ?? null,
      ),
      cierraCodigosEventos: new FormControl(codigo?.cierraCodigosEventos ?? []),
      flagsTrackers: new FormControl<FlagsTrackers[]>(
        codigo?.flagsTrackers ?? [],
      ),
      // categoriaEvento es populate, no se edita aquí
    });
  }

  private addCodigoFormGroupEntrada(codigo?: ICodigoDispositivoEntrada) {
    return new FormGroup({
      codigo: new FormControl(codigo?.codigo, [Validators.required]),
      descripcion: new FormControl(codigo?.descripcion, [Validators.required]),
      idCategoriaEvento: new FormControl(codigo?.idCategoriaEvento, [
        Validators.required,
      ]),
    });
  }

  codigoUnicoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formArray = control as FormArray;
      const codigosMap = new Map<string, number[]>();

      formArray.controls.forEach((group, index) => {
        const codigo = group.get('codigo')?.value?.trim();
        if (!codigo) return;

        const indices = codigosMap.get(codigo) || [];
        indices.push(index);
        codigosMap.set(codigo, indices);
      });

      let hayDuplicados = false;

      formArray.controls.forEach((group) => {
        const codigo = group.get('codigo')?.value?.trim();
        const indices = codigosMap.get(codigo);

        const control = group.get('codigo');
        if (indices && indices.length > 1) {
          control?.setErrors({
            ...(control.errors || {}),
            codigoRepetido: true,
          });
          hayDuplicados = true;
        } else if (control?.hasError('codigoRepetido')) {
          const newErrors = { ...control.errors };
          delete newErrors['codigoRepetido'];
          control.setErrors(Object.keys(newErrors).length ? newErrors : null);
        }
      });

      return hayDuplicados ? { codigoRepetido: true } : null;
    };
  }

  public deleteEvento(i: number) {
    this.codigosForm.removeAt(i);
  }

  public addCodigoEntrada() {
    this.codigosEntradaForm.push(this.addCodigoFormGroup());
  }

  public deleteEventoEntrada(i: number) {
    this.codigosEntradaForm.removeAt(i);
  }

  //
  public async onSubmit() {
    if (!this.form?.valid) {
      this.form?.markAllAsTouched();
      this.form?.updateValueAndValidity();
      const detalle = this.describirInvalidos();
      console.warn('Form inválido:', this.form?.errors, detalle);
      this.helper.notifError(`No se puede guardar: ${detalle}`);
      return;
    }
    this.loading = true;
    try {
      const data = this.getData();
      if (this.id) {
        // Update
        await this.service.update(this.id, data);
        this.helper.notifSuccess('Actualizado correctamente');
      } else {
        // Create
        await this.service.create(data);
        this.helper.notifSuccess('Creado correctamente');
      }
      this.helper.volver();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  private describirInvalidos(): string {
    const partes: string[] = [];
    if (this.form?.get('nombre')?.invalid) {
      partes.push('falta nombre');
    }
    const revisar = (fa: FormArray | undefined, etiqueta: string) => {
      if (!fa) return;
      fa.controls.forEach((g, i) => {
        if (!g.invalid) return;
        const camposFaltantes: string[] = [];
        ['codigo', 'descripcion', 'idCategoriaEvento'].forEach((c) => {
          const ctrl = g.get(c);
          if (ctrl?.hasError('required')) camposFaltantes.push(c);
          if (ctrl?.hasError('codigoRepetido'))
            camposFaltantes.push('código repetido');
        });
        if (camposFaltantes.length) {
          partes.push(`${etiqueta} #${i + 1}: ${camposFaltantes.join(', ')}`);
        }
      });
    };
    revisar(this.codigosForm, 'Código');
    if (this.tipo === 'Tracker')
      revisar(this.codigosEntradaForm, 'Código de entrada');
    return partes.length ? partes.join('; ') : 'revisar campos requeridos';
  }

  public getData(): ICreateCodigosDispositivo | IUpdateCodigosDispositivo {
    const data: ICreateCodigosDispositivo | IUpdateCodigosDispositivo =
      this.form?.value;

    // Si no es tipo Tracker, asegurarse de que codigosEntrada sea un array vacío
    if (this.tipo !== 'Tracker') {
      data.codigosEntrada = [];
    }

    return data;
  }

  public async listarCodigoDispositivo(): Promise<void> {
    if (this.id) {
      this.codigosDispositivo = await firstValueFrom(
        this.lista2.codigoDispositivo(this.id),
      );
    }
  }

  public async listarCategoriasEventos(): Promise<void> {
    const query: IQueryParam = {
      sort: 'nombre',
    };
    this.categoriasEventos$?.unsubscribe();
    this.categoriasEventos$ = this.lista2
      .categoriaEventos(query, 30)
      .subscribe((data) => {
        this.categoriasEventos = data.datos;
      });
  }

  private setTitles() {
    if (this.codigosDispositivo) {
      this.title = 'Editar códigos';
      this.subtitle = this.codigosDispositivo?.nombre;
    } else {
      this.title = 'Crear códigos';
    }
  }

  async ngOnInit() {
    this.loading = true;
    const params = await firstValueFrom(this.route.paramMap);
    this.id = params.get('id');

    this.tipo = (params.get('tipo') as TipoDispositivo) || 'Alarma';
    if (this.id) this.codigosDispositivo = await this.service.getById(this.id);

    const tipoFormateado =
      this.tipo === 'DispositivoLorawan'
        ? 'dispositivo lorawan'
        : this.tipo?.toLowerCase();

    this.title = this.id
      ? `Editar códigos de ${tipoFormateado}`
      : `Crear códigos de ${tipoFormateado}`;

    await Promise.all([
      this.listarCodigoDispositivo(),
      this.listarCategoriasEventos(),
    ]);

    // Poblar initialNames para el selector de descripción (modo edición)
    if (this.tipo === 'Tracker' && this.codigosDispositivo) {
      this.initialNamesForCodigos =
        this.codigosDispositivo.codigos?.map((c) =>
          c.descripcion ? [c.descripcion] : [],
        ) ?? [];
      this.initialNamesForCodigosEntrada =
        this.codigosDispositivo.codigosEntrada?.map((c) =>
          c.descripcion ? [c.descripcion] : [],
        ) ?? [];
    }

    this.setTitles();
    this.createForm();
    this.loading = false;
  }

  async ngOnDestroy() {
    this.categoriasEventos$?.unsubscribe();
  }
}
